import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Building2,
  CheckCircle2,
  Mail,
  MessageSquare,
  Network,
  RefreshCw,
  ShieldCheck,
  Ship,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  CIRCLE_TYPE_COLORS,
  CIRCLE_TYPE_LABELS,
  circleApi,
  networkAllianceApi,
  type AllianceActionRequest,
  type AllianceContract,
  type AllianceContractEntitlement,
  type AllianceConversationLedgerEntry,
  type AllianceOverview,
  type AllianceVolumeContribution,
  type Circle,
} from '@/lib/network-api';
import { cn } from '@/lib/utils';

const ALLIANCE_CIRCLE_TYPES = new Set(['consortium', 'agent_network']);
const ACTION_POLICY_OPTIONS: Array<{
  value: AllianceActionRequest['action_type'];
  label: string;
}> = [
  { value: 'allocation_request', label: 'Allocation Request' },
  { value: 'rate_override', label: 'Rate Override' },
  { value: 'capacity_release', label: 'Capacity Release' },
  { value: 'partner_onboarding', label: 'Partner Onboarding' },
  { value: 'exception_approval', label: 'Exception Approval' },
  { value: 'contract_activation', label: 'Contract Activation' },
];

function parseRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getAllianceApprovalPolicyOverride(
  config: Record<string, unknown> | null | undefined,
  actionType: AllianceActionRequest['action_type'],
) {
  const alliance = parseRecord(parseRecord(config)['alliance']);
  const approvals = parseRecord(alliance['approvals']);
  const actionPolicies = parseRecord(approvals['action_policies']);
  return parseRecord(actionPolicies[actionType]);
}

function getAllianceApprovalDefaults(
  config: Record<string, unknown> | null | undefined,
  actionType?: AllianceActionRequest['action_type'],
) {
  const alliance = parseRecord(parseRecord(config)['alliance']);
  const approvals = parseRecord(alliance['approvals']);
  const actionPolicy = actionType ? getAllianceApprovalPolicyOverride(config, actionType) : {};
  return {
    reviewerRole: typeof actionPolicy['reviewer_role'] === 'string' && actionPolicy['reviewer_role'].length > 0
      ? String(actionPolicy['reviewer_role'])
      : typeof approvals['default_reviewer_role'] === 'string' && approvals['default_reviewer_role'].length > 0
      ? String(approvals['default_reviewer_role'])
      : 'alliance_manager',
    escalationRole: typeof actionPolicy['escalation_role'] === 'string' && actionPolicy['escalation_role'].length > 0
      ? String(actionPolicy['escalation_role'])
      : typeof approvals['default_escalation_role'] === 'string' && approvals['default_escalation_role'].length > 0
      ? String(approvals['default_escalation_role'])
      : null,
    timeoutMinutes: typeof actionPolicy['timeout_minutes'] === 'number' && Number.isFinite(actionPolicy['timeout_minutes'])
      ? Number(actionPolicy['timeout_minutes'])
      : typeof approvals['default_timeout_minutes'] === 'number' && Number.isFinite(approvals['default_timeout_minutes'])
      ? Number(approvals['default_timeout_minutes'])
      : 240,
  };
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-SG');
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getSourceRailLabel(sourceType: AllianceConversationLedgerEntry['source_type']) {
  switch (sourceType) {
    case 'official_email': return 'Official Email';
    case 'network_dm': return 'Network DM';
    case 'network_channel': return 'Network Channel';
    case 'topic_thread': return 'Topic Thread';
    default: return 'System Note';
  }
}

function getActionLabel(actionType: AllianceActionRequest['action_type']) {
  switch (actionType) {
    case 'allocation_request': return 'Allocation';
    case 'rate_override': return 'Rate Override';
    case 'capacity_release': return 'Capacity Release';
    case 'partner_onboarding': return 'Partner Onboarding';
    case 'exception_approval': return 'Exception Approval';
    case 'contract_activation': return 'Contract Activation';
  }
}

function getContractKindLabel(kind: AllianceContract['contract_kind']) {
  switch (kind) {
    case 'master_rate': return 'Master Rate';
    case 'named_account': return 'Named Account';
    case 'bundle_account': return 'Bundle Account';
    case 'space_commitment': return 'Space Commitment';
    case 'service_agreement': return 'Service Agreement';
    case 'rebate_program': return 'Rebate Program';
  }
}

function MetricCard({
  title,
  value,
  hint,
  icon: Icon,
}: {
  title: string;
  value: string;
  hint: string;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-4 pt-5">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{title}</p>
          <p className="text-2xl font-semibold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-2.5">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function NetworkAlliancePage() {
  const [circles, setCircles] = useState<Circle[]>([]);
  const [selectedCircleId, setSelectedCircleId] = useState<string>('');
  const [overview, setOverview] = useState<AllianceOverview | null>(null);
  const [contracts, setContracts] = useState<AllianceContract[]>([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [entitlements, setEntitlements] = useState<AllianceContractEntitlement[]>([]);
  const [actions, setActions] = useState<AllianceActionRequest[]>([]);
  const [conversations, setConversations] = useState<AllianceConversationLedgerEntry[]>([]);
  const [volumes, setVolumes] = useState<AllianceVolumeContribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingFromIntake, setCreatingFromIntake] = useState(false);
  const [intakeCaseId, setIntakeCaseId] = useState('');
  const [intakeActionType, setIntakeActionType] = useState<AllianceActionRequest['action_type']>('exception_approval');
  const [intakePriority, setIntakePriority] = useState<AllianceActionRequest['priority']>('normal');
  const [intakeSubject, setIntakeSubject] = useState('');
  const [intakeSummary, setIntakeSummary] = useState('');
  const [creatingEntitlement, setCreatingEntitlement] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState<string | null>(null);
  const [readBusyId, setReadBusyId] = useState<string | null>(null);
  const [escalateBusy, setEscalateBusy] = useState(false);
  const [escalateAllBusy, setEscalateAllBusy] = useState(false);
  const [savingApprovalConfig, setSavingApprovalConfig] = useState(false);
  const [entitlementMemberId, setEntitlementMemberId] = useState('');
  const [entitlementType, setEntitlementType] = useState<AllianceContractEntitlement['entitlement_type']>('lane_access');
  const [entitlementAccessLevel, setEntitlementAccessLevel] = useState<AllianceContractEntitlement['access_level']>('use');
  const [configReviewerRole, setConfigReviewerRole] = useState('alliance_manager');
  const [configEscalationRole, setConfigEscalationRole] = useState('');
  const [configTimeoutMinutes, setConfigTimeoutMinutes] = useState('240');
  const [policyActionType, setPolicyActionType] = useState<AllianceActionRequest['action_type']>('exception_approval');
  const [policyReviewerRole, setPolicyReviewerRole] = useState('');
  const [policyEscalationRole, setPolicyEscalationRole] = useState('');
  const [policyTimeoutMinutes, setPolicyTimeoutMinutes] = useState('');
  const [savingActionPolicy, setSavingActionPolicy] = useState(false);

  const allianceCircles = useMemo(
    () => circles.filter((circle) => ALLIANCE_CIRCLE_TYPES.has(circle.circle_type)),
    [circles],
  );

  const loadCircles = useCallback(async () => {
    const allCircles = await circleApi.list();
    setCircles(allCircles);
    const candidate = allCircles.find((circle) => ALLIANCE_CIRCLE_TYPES.has(circle.circle_type));
    setSelectedCircleId((current) => current || candidate?.id || '');
    return allCircles;
  }, []);

  const loadWorkspace = useCallback(async (circleId: string) => {
    if (!circleId) {
      setOverview(null);
      setContracts([]);
      setActions([]);
      setConversations([]);
      setVolumes([]);
      return;
    }
    const [overviewData, contractRows, actionRows, conversationRows, volumeRows] = await Promise.all([
      networkAllianceApi.overview(circleId),
      networkAllianceApi.contracts(circleId),
      networkAllianceApi.actionRequests(circleId),
      networkAllianceApi.conversationLedger(circleId, { limit: 8 }),
      networkAllianceApi.volumeContributions(circleId),
    ]);
    setOverview(overviewData);
    setContracts(contractRows);
    setActions(actionRows);
    setConversations(conversationRows);
    setVolumes(volumeRows);
  }, []);

  const reload = useCallback(async () => {
    setRefreshing(true);
    try {
      const allCircles = await loadCircles();
      const nextCircleId = selectedCircleId || allCircles.find((circle) => ALLIANCE_CIRCLE_TYPES.has(circle.circle_type))?.id;
      if (nextCircleId) {
        await loadWorkspace(nextCircleId);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load alliance circles');
    } finally {
      setRefreshing(false);
    }
  }, [loadCircles, loadWorkspace, selectedCircleId]);

  useEffect(() => {
    setLoading(true);
    loadCircles()
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to load alliance circles');
      })
      .finally(() => setLoading(false));
  }, [loadCircles]);

  useEffect(() => {
    if (!selectedCircleId) return;
    setRefreshing(true);
    loadWorkspace(selectedCircleId)
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to load alliance workspace');
      })
      .finally(() => setRefreshing(false));
  }, [loadWorkspace, selectedCircleId]);

  const selectedCircle = useMemo(
    () => allianceCircles.find((circle) => circle.id === selectedCircleId) ?? null,
    [allianceCircles, selectedCircleId],
  );
  const approvalDefaults = useMemo(
    () => getAllianceApprovalDefaults(selectedCircle?.config ?? null),
    [selectedCircle?.config],
  );
  const selectedActionPolicyOverride = useMemo(
    () => getAllianceApprovalPolicyOverride(selectedCircle?.config ?? null, policyActionType),
    [policyActionType, selectedCircle?.config],
  );
  const selectedActionPolicyEffective = useMemo(
    () => getAllianceApprovalDefaults(selectedCircle?.config ?? null, policyActionType),
    [policyActionType, selectedCircle?.config],
  );

  useEffect(() => {
    setConfigReviewerRole(approvalDefaults.reviewerRole);
    setConfigEscalationRole(approvalDefaults.escalationRole ?? '');
    setConfigTimeoutMinutes(String(approvalDefaults.timeoutMinutes));
  }, [approvalDefaults.escalationRole, approvalDefaults.reviewerRole, approvalDefaults.timeoutMinutes]);

  useEffect(() => {
    setPolicyReviewerRole(typeof selectedActionPolicyOverride['reviewer_role'] === 'string' ? String(selectedActionPolicyOverride['reviewer_role']) : '');
    setPolicyEscalationRole(typeof selectedActionPolicyOverride['escalation_role'] === 'string' ? String(selectedActionPolicyOverride['escalation_role']) : '');
    setPolicyTimeoutMinutes(
      typeof selectedActionPolicyOverride['timeout_minutes'] === 'number' && Number.isFinite(selectedActionPolicyOverride['timeout_minutes'])
        ? String(selectedActionPolicyOverride['timeout_minutes'])
        : '',
    );
  }, [selectedActionPolicyOverride]);

  const topContracts = contracts.slice(0, 5);
  const topActions = actions.slice(0, 6);
  const topConversations = conversations.slice(0, 6);
  const topVolumes = volumes.slice(0, 6);
  const selectedContract = useMemo(
    () => contracts.find((contract) => contract.id === selectedContractId) ?? topContracts[0] ?? null,
    [contracts, selectedContractId, topContracts],
  );

  const loadEntitlements = useCallback(async (contractId: string) => {
    if (!contractId) {
      setEntitlements([]);
      return;
    }
    const rows = await networkAllianceApi.entitlements(contractId);
    setEntitlements(rows);
  }, []);

  useEffect(() => {
    if (!selectedContractId && topContracts[0]?.id) {
      setSelectedContractId(topContracts[0].id);
    }
  }, [selectedContractId, topContracts]);

  useEffect(() => {
    if (!selectedContract?.id) return;
    loadEntitlements(selectedContract.id).catch((error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to load contract entitlements');
    });
  }, [loadEntitlements, selectedContract?.id]);

  const handleCreateActionFromIntake = useCallback(async () => {
    if (!selectedCircleId || !intakeCaseId.trim()) {
      toast.error('Intake case ID is required');
      return;
    }
    setCreatingFromIntake(true);
    try {
      await networkAllianceApi.createActionFromIntake(selectedCircleId, intakeCaseId.trim(), {
        action_type: intakeActionType,
        priority: intakePriority,
        subject: intakeSubject.trim() || undefined,
        summary: intakeSummary.trim() || undefined,
      });
      toast.success('Alliance action created from intake case');
      setIntakeCaseId('');
      setIntakeSubject('');
      setIntakeSummary('');
      await loadWorkspace(selectedCircleId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create alliance action from intake');
    } finally {
      setCreatingFromIntake(false);
    }
  }, [intakeActionType, intakeCaseId, intakePriority, intakeSubject, intakeSummary, loadWorkspace, selectedCircleId]);

  const handleCreateEntitlement = useCallback(async () => {
    if (!selectedContract || !entitlementMemberId.trim()) {
      toast.error('Contract and member ID are required');
      return;
    }
    setCreatingEntitlement(true);
    try {
      await networkAllianceApi.createEntitlement(selectedContract.id, {
        member_id: entitlementMemberId.trim(),
        entitlement_type: entitlementType,
        access_level: entitlementAccessLevel,
      });
      toast.success('Alliance entitlement created');
      setEntitlementMemberId('');
      await loadEntitlements(selectedContract.id);
      if (selectedCircleId) await loadWorkspace(selectedCircleId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create alliance entitlement');
    } finally {
      setCreatingEntitlement(false);
    }
  }, [entitlementAccessLevel, entitlementMemberId, entitlementType, loadEntitlements, loadWorkspace, selectedCircleId, selectedContract]);

  const handleRequestApproval = useCallback(async (action: AllianceActionRequest) => {
    setApprovalBusyId(action.id);
    try {
      const actionApprovalDefaults = getAllianceApprovalDefaults(selectedCircle?.config ?? null, action.action_type);
      await networkAllianceApi.requestApproval(action.id, {
        reviewer_role: actionApprovalDefaults.reviewerRole,
        requester_role: 'alliance_operator',
        priority: action.priority === 'critical' ? 'P0' : action.priority === 'high' ? 'P1' : 'P2',
        timeout_minutes: action.priority === 'critical' ? 60 : actionApprovalDefaults.timeoutMinutes,
        escalation_role: actionApprovalDefaults.escalationRole ?? undefined,
        title: `Alliance ${getActionLabel(action.action_type)} approval`,
      });
      toast.success('Alliance approval requested');
      if (selectedCircleId) await loadWorkspace(selectedCircleId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to request approval');
    } finally {
      setApprovalBusyId(null);
    }
  }, [loadWorkspace, selectedCircle?.config, selectedCircleId]);

  const handleMarkAttentionRead = useCallback(async (actionId: string) => {
    setReadBusyId(actionId);
    try {
      await networkAllianceApi.markAttentionRead(actionId);
      if (selectedCircleId) await loadWorkspace(selectedCircleId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to mark action attention read');
    } finally {
      setReadBusyId(null);
    }
  }, [loadWorkspace, selectedCircleId]);

  const handleEscalateOverdue = useCallback(async () => {
    if (!selectedCircleId) return;
    setEscalateBusy(true);
    try {
      const result = await networkAllianceApi.escalateOverdue(selectedCircleId);
      toast.success(`Escalated ${result.escalated} overdue action(s)`);
      await loadWorkspace(selectedCircleId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to escalate overdue actions');
    } finally {
      setEscalateBusy(false);
    }
  }, [loadWorkspace, selectedCircleId]);

  const handleEscalateManagedOverdue = useCallback(async () => {
    setEscalateAllBusy(true);
    try {
      const result = await networkAllianceApi.escalateManagedOverdue();
      toast.success(`Escalated ${result.escalated} overdue action(s) across ${result.circles.length} alliance(s)`);
      await reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to escalate managed overdue actions');
    } finally {
      setEscalateAllBusy(false);
    }
  }, [reload]);

  const handleSaveApprovalConfig = useCallback(async () => {
    if (!selectedCircle) return;
    const timeoutMinutes = Math.max(5, Number.parseInt(configTimeoutMinutes, 10) || 240);
    setSavingApprovalConfig(true);
    try {
      await circleApi.update(selectedCircle.id, {
        config: {
          ...parseRecord(selectedCircle.config),
          alliance: {
            ...parseRecord(parseRecord(selectedCircle.config)['alliance']),
            approvals: {
              ...parseRecord(parseRecord(parseRecord(selectedCircle.config)['alliance'])['approvals']),
              default_reviewer_role: configReviewerRole.trim() || 'alliance_manager',
              default_escalation_role: configEscalationRole.trim() || null,
              default_timeout_minutes: timeoutMinutes,
            },
          },
        },
      });
      toast.success('Alliance approval defaults saved');
      await reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save approval defaults');
    } finally {
      setSavingApprovalConfig(false);
    }
  }, [configEscalationRole, configReviewerRole, configTimeoutMinutes, reload, selectedCircle]);

  const handleSaveActionPolicy = useCallback(async () => {
    if (!selectedCircle) return;
    setSavingActionPolicy(true);
    try {
      const currentConfig = parseRecord(selectedCircle.config);
      const allianceConfig = parseRecord(currentConfig['alliance']);
      const approvalConfig = parseRecord(allianceConfig['approvals']);
      const parsedTimeoutMinutes = Number.parseInt(policyTimeoutMinutes, 10);
      const actionPolicies = {
        ...parseRecord(approvalConfig['action_policies']),
        [policyActionType]: {
          reviewer_role: policyReviewerRole.trim() || null,
          escalation_role: policyEscalationRole.trim() || null,
          timeout_minutes: Number.isFinite(parsedTimeoutMinutes) && parsedTimeoutMinutes >= 5
            ? parsedTimeoutMinutes
            : null,
        },
      };
      await circleApi.update(selectedCircle.id, {
        config: {
          ...currentConfig,
          alliance: {
            ...allianceConfig,
            approvals: {
              ...approvalConfig,
              action_policies: actionPolicies,
            },
          },
        },
      });
      toast.success(`Saved action policy for ${getActionLabel(policyActionType)}`);
      await reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save action policy');
    } finally {
      setSavingActionPolicy(false);
    }
  }, [
    policyActionType,
    policyEscalationRole,
    policyReviewerRole,
    policyTimeoutMinutes,
    reload,
    selectedCircle,
  ]);

  const handleClearActionPolicy = useCallback(async () => {
    if (!selectedCircle) return;
    setSavingActionPolicy(true);
    try {
      const currentConfig = parseRecord(selectedCircle.config);
      const allianceConfig = parseRecord(currentConfig['alliance']);
      const approvalConfig = parseRecord(allianceConfig['approvals']);
      const actionPolicies = {
        ...parseRecord(approvalConfig['action_policies']),
      };
      delete actionPolicies[policyActionType];
      await circleApi.update(selectedCircle.id, {
        config: {
          ...currentConfig,
          alliance: {
            ...allianceConfig,
            approvals: {
              ...approvalConfig,
              action_policies: actionPolicies,
            },
          },
        },
      });
      toast.success(`Cleared action policy for ${getActionLabel(policyActionType)}`);
      await reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clear action policy');
    } finally {
      setSavingActionPolicy(false);
    }
  }, [policyActionType, reload, selectedCircle]);

  if (loading) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28 w-full" />)}
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (allianceCircles.length === 0) {
    return (
      <div className="flex flex-col gap-5 p-4 md:p-6">
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-start gap-4 py-10">
            <div className="rounded-2xl border border-border bg-muted/40 p-3">
              <Network className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold">Alliance Workspace</h2>
              <p className="max-w-2xl text-sm text-muted-foreground">
                This workspace activates when you have at least one `consortium` or `agent_network` circle.
                Build the network first, then use Alliance OS to run consolidated contracts, governed actions,
                and dual-rail communication.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/network/circles">Open Circles</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/network/directory">Find Network Members</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="h-6 rounded-full px-2.5 uppercase tracking-[0.12em]">
              Alliance OS
            </Badge>
            {selectedCircle ? (
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', CIRCLE_TYPE_COLORS[selectedCircle.circle_type])}>
                {CIRCLE_TYPE_LABELS[selectedCircle.circle_type]}
              </span>
            ) : null}
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Alliance Workspace</h1>
            <p className="text-sm text-muted-foreground">
              LP Global-style HQ workspace for consolidated buying, governed allocation, official email trace,
              and partner action routing.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select value={selectedCircleId} onValueChange={setSelectedCircleId}>
            <SelectTrigger className="min-w-[260px]">
              <SelectValue placeholder="Select alliance" />
            </SelectTrigger>
            <SelectContent>
              {allianceCircles.map((circle) => (
                <SelectItem key={circle.id} value={circle.id}>
                  {circle.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
            <Button variant="outline" onClick={reload} disabled={refreshing}>
              <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleEscalateOverdue} disabled={escalateBusy || !selectedCircleId}>
              {escalateBusy ? 'Escalating…' : 'Escalate Overdue'}
            </Button>
            <Button variant="outline" onClick={handleEscalateManagedOverdue} disabled={escalateAllBusy}>
              {escalateAllBusy ? 'Escalating All…' : 'Escalate All Managed'}
            </Button>
          </div>
        </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Members" value={String(overview?.member_count ?? 0)} hint="Active alliance participants" icon={Users} />
        <MetricCard title="Active Contracts" value={String(overview?.active_contracts ?? 0)} hint={`${overview?.contracts ?? 0} contracts on file`} icon={Building2} />
        <MetricCard title="Monthly TEU" value={String(overview?.monthly_teu ?? 0)} hint="Current period contribution ledger" icon={Ship} />
        <MetricCard title="Open Actions" value={String(overview?.open_actions ?? 0)} hint={`${overview?.recent_conversations ?? 0} recent conversation records`} icon={ShieldCheck} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Master Contracts</CardTitle>
              <p className="text-sm text-muted-foreground">
                HQ-negotiated contracts and entitlement-ready commercial frames.
              </p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/network/circles">
                Manage Circle
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {topContracts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                No alliance contracts yet. This is where master rates, named accounts, bundle accounts,
                and space commitments will surface.
              </div>
            ) : topContracts.map((contract) => (
              <button
                key={contract.id}
                type="button"
                onClick={() => setSelectedContractId(contract.id)}
                className={cn(
                  'w-full rounded-xl border border-border bg-background px-4 py-3 text-left',
                  selectedContract?.id === contract.id && 'border-primary/40 bg-primary/5',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{contract.contract_name}</p>
                      <Badge variant={contract.status === 'active' ? 'default' : 'outline'}>
                        {contract.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {contract.contract_code} · {getContractKindLabel(contract.contract_kind)} · {contract.counterparty_name}
                    </p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{formatDate(contract.effective_from)} → {formatDate(contract.effective_to)}</p>
                    <p>{contract.counterparty_type}</p>
                  </div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contribution Signals</CardTitle>
            <p className="text-sm text-muted-foreground">
              Latest volume rows feeding the consolidated buying power engine.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {topVolumes.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                No contribution rows yet. Tressa-like intake and ERP sync can later feed this ledger automatically.
              </div>
            ) : topVolumes.map((row) => (
              <div key={row.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
                <div className="space-y-1">
                  <p className="font-medium">{row.pol_code ?? 'N/A'} → {row.pod_code ?? 'N/A'}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.mode} · {row.direction} · {row.carrier_name ?? 'carrier pending'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">{row.teu} TEU</p>
                  <p className="text-xs text-muted-foreground">{formatDate(row.contribution_period)}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_1fr]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Governed Action Queue</CardTitle>
              <p className="text-sm text-muted-foreground">
                Open actions that should later route into Approval, PWA, and AI review.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/approvals">
                Review Path
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {topActions.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                No open actions. When partners request allocations, overrides, onboarding, or exceptions,
                they will land here with evidence and status.
              </div>
            ) : topActions.map((action) => (
              <div key={action.id} className="rounded-xl border border-border px-4 py-3">
                {(() => {
                  const actionPolicy = getAllianceApprovalDefaults(selectedCircle?.config ?? null, action.action_type);
                  return (
                    <>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{getActionLabel(action.action_type)}</p>
                      <Badge variant={action.priority === 'critical' ? 'destructive' : 'outline'}>
                        {action.priority}
                      </Badge>
                      <Badge variant="secondary">{action.status}</Badge>
                      {action.approval_status ? (
                        <Badge variant={action.approval_status === 'approved' ? 'default' : action.approval_status === 'rejected' ? 'destructive' : 'outline'}>
                          approval {action.approval_status}
                        </Badge>
                      ) : null}
                      {action.sla_state && action.sla_state !== 'none' ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            action.sla_state === 'overdue' && 'border-red-200 bg-red-50 text-red-700',
                            action.sla_state === 'due_soon' && 'border-amber-200 bg-amber-50 text-amber-700',
                            action.sla_state === 'on_track' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
                          )}
                        >
                          {action.sla_state === 'overdue' ? 'SLA overdue' : action.sla_state === 'due_soon' ? 'SLA due soon' : action.sla_state}
                        </Badge>
                      ) : null}
                      {action.has_unread_notification ? (
                        <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                          {action.my_unread_notification_count ?? 0} unread
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Due {formatDateTime(action.due_at)} · {action.evidence_pack?.length ?? 0} evidence item(s)
                      {action.hours_to_due !== undefined && action.hours_to_due !== null ? ` · ${action.hours_to_due}h to due` : ''}
                    </p>
                  </div>
                  <Activity className="mt-1 h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Policy reviewer `{actionPolicy.reviewerRole}` · timeout {actionPolicy.timeoutMinutes} min
                  {actionPolicy.escalationRole ? ` · escalation ${actionPolicy.escalationRole}` : ''}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {!action.approval_request_id && !['approved', 'rejected', 'completed', 'cancelled'].includes(action.status) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={approvalBusyId === action.id}
                      onClick={() => void handleRequestApproval(action)}
                    >
                      {approvalBusyId === action.id ? 'Routing…' : 'Push Approval'}
                    </Button>
                  ) : null}
                  {action.approval_request_id ? (
                    <Button size="sm" variant="ghost" asChild>
                      <Link to="/approvals">Open Approval Queue</Link>
                    </Button>
                  ) : null}
                  {action.has_unread_notification ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={readBusyId === action.id}
                      onClick={() => void handleMarkAttentionRead(action.id)}
                    >
                      {readBusyId === action.id ? 'Marking…' : 'Mark Read'}
                    </Button>
                  ) : null}
                </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Dual-Rail Conversation Ledger</CardTitle>
              <p className="text-sm text-muted-foreground">
                Official email where needed, in-network collaboration where possible.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/network/messages">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Messages
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {topConversations.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                No conversation ledger entries yet. This view will unify official email, network DM, and thread evidence
                against contracts, action requests, and shipments.
              </div>
            ) : topConversations.map((entry) => (
              <div key={entry.id} className="rounded-xl border border-border px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={entry.source_type === 'official_email' ? 'default' : 'outline'}>
                        {entry.source_type === 'official_email' ? <Mail className="mr-1 h-3 w-3" /> : <MessageSquare className="mr-1 h-3 w-3" />}
                        {getSourceRailLabel(entry.source_type)}
                      </Badge>
                      <Badge variant="secondary">{entry.direction}</Badge>
                    </div>
                    <p className="font-medium">{entry.subject ?? `${entry.related_object_type} update`}</p>
                    <p className="line-clamp-2 text-sm text-muted-foreground">
                      {entry.summary ?? 'No summary captured yet.'}
                    </p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{formatDateTime(entry.created_at)}</p>
                    <p>{entry.evidence_pack?.length ?? 0} evidence</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Contract Detail & Entitlements</CardTitle>
            <p className="text-sm text-muted-foreground">
              Entitlement layer for member-specific usage, named account access, and controlled contract visibility.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedContract ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                Select a contract to inspect and grant entitlements.
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-border bg-background px-4 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-lg font-semibold">{selectedContract.contract_name}</p>
                    <Badge variant={selectedContract.status === 'active' ? 'default' : 'outline'}>
                      {selectedContract.status}
                    </Badge>
                    <Badge variant="secondary">{getContractKindLabel(selectedContract.contract_kind)}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {selectedContract.contract_code} · {selectedContract.counterparty_name} · {selectedContract.counterparty_type}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Effective {formatDate(selectedContract.effective_from)} → {formatDate(selectedContract.effective_to)}
                  </p>
                </div>

                <div className="space-y-3">
                  {entitlements.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                      No entitlements yet. Grant the first member access on the right.
                    </div>
                  ) : entitlements.map((entitlement) => (
                    <div key={entitlement.id} className="rounded-xl border border-border px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">
                          {entitlement.member_external_name ?? entitlement.member_tenant_id ?? entitlement.member_id}
                        </p>
                        <Badge variant="outline">{entitlement.entitlement_type}</Badge>
                        <Badge variant="secondary">{entitlement.access_level}</Badge>
                        <Badge variant={entitlement.status === 'active' ? 'default' : 'outline'}>{entitlement.status}</Badge>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Member role: {entitlement.member_role ?? 'unknown'} · Effective {formatDate(entitlement.effective_from)} → {formatDate(entitlement.effective_to)}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Grant Entitlement</CardTitle>
            <p className="text-sm text-muted-foreground">
              Minimal phase-1 form. Use a `network_circle_members.id` from the selected alliance circle.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="alliance-entitlement-member-id">Circle Member ID</Label>
              <Input
                id="alliance-entitlement-member-id"
                placeholder="Paste network_circle_members.id"
                value={entitlementMemberId}
                onChange={(event) => setEntitlementMemberId(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-entitlement-type">Entitlement Type</Label>
              <Select value={entitlementType} onValueChange={(value) => setEntitlementType(value as AllianceContractEntitlement['entitlement_type'])}>
                <SelectTrigger id="alliance-entitlement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lane_access">Lane Access</SelectItem>
                  <SelectItem value="named_account_access">Named Account Access</SelectItem>
                  <SelectItem value="bundle_account_access">Bundle Account Access</SelectItem>
                  <SelectItem value="space_allocation">Space Allocation</SelectItem>
                  <SelectItem value="rebate_share">Rebate Share</SelectItem>
                  <SelectItem value="pricing_visibility">Pricing Visibility</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-entitlement-access">Access Level</Label>
              <Select value={entitlementAccessLevel} onValueChange={(value) => setEntitlementAccessLevel(value as AllianceContractEntitlement['access_level'])}>
                <SelectTrigger id="alliance-entitlement-access">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View</SelectItem>
                  <SelectItem value="use">Use</SelectItem>
                  <SelectItem value="propose_override">Propose Override</SelectItem>
                  <SelectItem value="manage">Manage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
              This also writes a `system_note` to the alliance conversation ledger, so HQ has an audit trail for member access grants.
            </div>
            <Button onClick={handleCreateEntitlement} disabled={creatingEntitlement || !selectedContract} className="w-full">
              {creatingEntitlement ? 'Granting…' : 'Grant Entitlement'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Approval Defaults</CardTitle>
            <p className="text-sm text-muted-foreground">
              Stored in circle config and used as the Alliance OS default reviewer / escalation policy.
            </p>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="alliance-default-reviewer-role">Default Reviewer Role</Label>
              <Input
                id="alliance-default-reviewer-role"
                value={configReviewerRole}
                onChange={(event) => setConfigReviewerRole(event.target.value)}
                placeholder="alliance_manager"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-default-escalation-role">Escalation Role</Label>
              <Input
                id="alliance-default-escalation-role"
                value={configEscalationRole}
                onChange={(event) => setConfigEscalationRole(event.target.value)}
                placeholder="regional_head"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-default-timeout">Timeout Minutes</Label>
              <Input
                id="alliance-default-timeout"
                inputMode="numeric"
                value={configTimeoutMinutes}
                onChange={(event) => setConfigTimeoutMinutes(event.target.value)}
                placeholder="240"
              />
            </div>
            <div className="md:col-span-3 flex flex-wrap items-center gap-3">
              <Button onClick={handleSaveApprovalConfig} disabled={savingApprovalConfig || !selectedCircle}>
                {savingApprovalConfig ? 'Saving…' : 'Save Approval Defaults'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Current effective defaults: reviewer `{approvalDefaults.reviewerRole}`, timeout {approvalDefaults.timeoutMinutes} min
                {approvalDefaults.escalationRole ? `, escalation ${approvalDefaults.escalationRole}` : ''}.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Action Policy Overrides</CardTitle>
            <p className="text-sm text-muted-foreground">
              Override reviewer, escalation, and timeout per alliance action type. Blank values inherit global defaults.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="alliance-policy-action-type">Action Type</Label>
              <Select value={policyActionType} onValueChange={(value) => setPolicyActionType(value as AllianceActionRequest['action_type'])}>
                <SelectTrigger id="alliance-policy-action-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_POLICY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-policy-reviewer-role">Reviewer Role Override</Label>
              <Input
                id="alliance-policy-reviewer-role"
                value={policyReviewerRole}
                onChange={(event) => setPolicyReviewerRole(event.target.value)}
                placeholder={`inherit ${approvalDefaults.reviewerRole}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-policy-escalation-role">Escalation Role Override</Label>
              <Input
                id="alliance-policy-escalation-role"
                value={policyEscalationRole}
                onChange={(event) => setPolicyEscalationRole(event.target.value)}
                placeholder={approvalDefaults.escalationRole ?? 'inherit none'}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-policy-timeout">Timeout Override</Label>
              <Input
                id="alliance-policy-timeout"
                inputMode="numeric"
                value={policyTimeoutMinutes}
                onChange={(event) => setPolicyTimeoutMinutes(event.target.value)}
                placeholder={`inherit ${approvalDefaults.timeoutMinutes}`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={handleSaveActionPolicy} disabled={savingActionPolicy || !selectedCircle}>
                {savingActionPolicy ? 'Saving…' : 'Save Action Policy'}
              </Button>
              <Button variant="outline" onClick={handleClearActionPolicy} disabled={savingActionPolicy || !selectedCircle}>
                Clear Override
              </Button>
            </div>
            <div className="rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
              Effective policy for `{getActionLabel(policyActionType)}`:
              <br />
              reviewer `{selectedActionPolicyEffective.reviewerRole}`, timeout {selectedActionPolicyEffective.timeoutMinutes} min
              {selectedActionPolicyEffective.escalationRole ? `, escalation ${selectedActionPolicyEffective.escalationRole}` : ''}.
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>SLA Notes</CardTitle>
            <p className="text-sm text-muted-foreground">
              Phase-1 treats `due_at` as the action SLA source and exposes overdue escalation as an operator-triggered workflow.
            </p>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="rounded-xl border border-border px-4 py-3">
              `on_track`: due more than 4 hours away.
            </div>
            <div className="rounded-xl border border-border px-4 py-3">
              `due_soon`: due within the next 4 hours.
            </div>
            <div className="rounded-xl border border-border px-4 py-3">
              `overdue`: due time already passed and can be escalated from HQ.
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-muted/20">
        <CardContent className="flex flex-col gap-4 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              Current Phase-1 coverage
            </div>
            <p className="text-sm text-muted-foreground">
              Phase-1 now covers the HQ workspace, intake-linked action creation, contract entitlements,
              and a PWA-first action center with mobile approve/reject handling.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <Link to="/network/messages">Open In-Network Messaging</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/inbox/email-messages">Open Official Email Inbox</Link>
            </Button>
            <Button asChild>
              <Link to="/network/circles">Manage Alliance Circles</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Intake To Alliance Action</CardTitle>
          <p className="text-sm text-muted-foreground">
            Use an existing `intake_cases.id` to create an alliance action request and auto-write an official communication
            ledger entry with intake evidence.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[1.1fr_0.8fr]">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="alliance-intake-case-id">Intake Case ID</Label>
              <Input
                id="alliance-intake-case-id"
                placeholder="Paste intake_cases.id"
                value={intakeCaseId}
                onChange={(event) => setIntakeCaseId(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-intake-subject">Subject Override</Label>
              <Input
                id="alliance-intake-subject"
                placeholder="Optional subject override"
                value={intakeSubject}
                onChange={(event) => setIntakeSubject(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-intake-summary">Summary Override</Label>
              <Textarea
                id="alliance-intake-summary"
                rows={4}
                placeholder="Optional summary override for conversation ledger"
                value={intakeSummary}
                onChange={(event) => setIntakeSummary(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-muted/20 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="alliance-intake-action-type">Action Type</Label>
              <Select value={intakeActionType} onValueChange={(value) => setIntakeActionType(value as AllianceActionRequest['action_type'])}>
                <SelectTrigger id="alliance-intake-action-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="allocation_request">Allocation Request</SelectItem>
                  <SelectItem value="rate_override">Rate Override</SelectItem>
                  <SelectItem value="capacity_release">Capacity Release</SelectItem>
                  <SelectItem value="partner_onboarding">Partner Onboarding</SelectItem>
                  <SelectItem value="exception_approval">Exception Approval</SelectItem>
                  <SelectItem value="contract_activation">Contract Activation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alliance-intake-priority">Priority</Label>
              <Select value={intakePriority} onValueChange={(value) => setIntakePriority(value as AllianceActionRequest['priority'])}>
                <SelectTrigger id="alliance-intake-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-xl border border-dashed border-border bg-background px-3 py-3 text-xs text-muted-foreground">
              This writes:
              <br />
              `intake_cases` linkage metadata
              <br />
              `alliance_action_requests`
              <br />
              `alliance_conversation_ledger`
            </div>
            <Button onClick={handleCreateActionFromIntake} disabled={creatingFromIntake || !selectedCircleId} className="w-full">
              {creatingFromIntake ? 'Creating…' : 'Create From Intake'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
